import { Button } from "@/components/ui/button";
import { ArrowRight, Trash2, Smartphone, UserPlus } from "lucide-react";

const HeroSectionV2 = () => {
  return (
    <section className="relative min-h-screen flex items-center bg-qcard-dark overflow-hidden">
      {/* Spotlight effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
      
      <div className="container relative z-10 mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/20 text-destructive text-sm font-medium border border-destructive/30">
              <span className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
              The uncomfortable truth about networking
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
              They Met You.
              <br />
              They Liked You.
              <br />
              <span className="text-white/40">They Forgot You.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Every day, promising connections vanish into thin air. Paper cards get tossed. Digital cards sit unopened. 
              <strong className="text-white"> But QCard? It goes straight into their phone</strong> — where you'll never be forgotten again.
            </p>

            <div className="flex items-center gap-4 justify-center lg:justify-start text-white/80">
              <span className="font-semibold text-primary">Be unforgettable.</span>
              <span className="w-1 h-1 bg-white/40 rounded-full" />
              <span className="font-semibold text-primary">Be saved.</span>
              <span className="w-1 h-1 bg-white/40 rounded-full" />
              <span className="font-semibold text-primary">Be called.</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all">
                Get Your Free QCard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6 bg-transparent">
                See the Difference
              </Button>
            </div>
          </div>
          
          {/* Right Content - Before/After Visual */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Before - Paper Card in Trash */}
              <div className="absolute -left-8 top-8 w-48 opacity-60">
                <div className="relative">
                  <div className="w-40 h-24 bg-white rounded-lg shadow-lg transform -rotate-12 flex items-center justify-center p-4">
                    <div className="text-left w-full">
                      <div className="h-2 w-16 bg-slate-300 rounded mb-2" />
                      <div className="h-1.5 w-12 bg-slate-200 rounded mb-1" />
                      <div className="h-1.5 w-20 bg-slate-200 rounded" />
                    </div>
                  </div>
                  {/* Trash overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-destructive/90 flex items-center justify-center">
                      <Trash2 className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <p className="text-center text-destructive text-sm mt-4 font-medium">Forgotten</p>
                </div>
              </div>

              {/* After - QCard Saved */}
              <div className="relative floating">
                {/* Phone Frame */}
                <div className="relative w-72 md:w-80 h-[580px] md:h-[640px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-primary/20 border border-white/10">
                  {/* Phone Screen */}
                  <div className="w-full h-full bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="flex justify-between items-center px-6 pt-4 pb-2 text-white/80 text-xs">
                      <span>9:41</span>
                      <div className="w-20 h-6 bg-black rounded-full" />
                      <div className="flex gap-1">
                        <div className="w-4 h-2 bg-white/60 rounded-sm" />
                      </div>
                    </div>
                    
                    {/* Contacts Header */}
                    <div className="px-6 py-3 border-b border-white/10">
                      <p className="text-white/60 text-sm">Contacts</p>
                    </div>
                    
                    {/* Contact List */}
                    <div className="px-4 py-3 space-y-2">
                      {/* Other contacts (faded) */}
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                        <div className="w-10 h-10 rounded-full bg-slate-700" />
                        <div>
                          <div className="h-2 w-20 bg-slate-700 rounded" />
                          <div className="h-1.5 w-14 bg-slate-800 rounded mt-1" />
                        </div>
                      </div>
                      
                      {/* YOUR CARD - Highlighted */}
                      <div className="relative">
                        <div className="absolute -inset-1 bg-primary/30 rounded-2xl blur-sm" />
                        <div className="relative flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-qcard-coral flex items-center justify-center text-white font-bold">
                            JD
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-semibold">John Doe</p>
                            <p className="text-white/60 text-sm">CEO, Acme Corp</p>
                          </div>
                          <Smartphone className="w-5 h-5 text-primary" />
                        </div>
                        <div className="absolute -right-2 -top-2">
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <UserPlus className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Other contacts (faded) */}
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                        <div className="w-10 h-10 rounded-full bg-slate-700" />
                        <div>
                          <div className="h-2 w-24 bg-slate-700 rounded" />
                          <div className="h-1.5 w-16 bg-slate-800 rounded mt-1" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                        <div className="w-10 h-10 rounded-full bg-slate-700" />
                        <div>
                          <div className="h-2 w-16 bg-slate-700 rounded" />
                          <div className="h-1.5 w-20 bg-slate-800 rounded mt-1" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Saved badge */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
                      <div className="px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium">
                        ✓ Saved to Contacts
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Glow Effect */}
                <div className="absolute -inset-10 bg-primary/10 rounded-full blur-3xl -z-10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionV2;
